import React, { useState, useEffect } from "react";
import FieldGroup from "../../inputs/fieldGroup";
import PropTypes from "prop-types";
import Select from "../../inputs/select";
import Radio from "../../inputs/radio";
import CriterionSummary from "./CriterionSummary";
import { getSubCohorts } from "../../../services/cohort";
import * as CONST from "../../../utilities/constants";
import "./SubCohortsCard.scss";

/**
 * SubCohortsCard - Criteria card for targeting users from other cohorts
 *
 * Data format: {
 *   cohorts: [{ unique_id: "...", name: "..." }],
 *   action: "include" | "exclude"
 * }
 */
function SubCohortsCard({ data, onChange, isReadOnly, isCollapsed, tagId, amsFlavor, modelingEnabled, cardId, contextualOnly, onSubCohortMetadataLoaded }) {
	const [subCohortOptions, setSubCohortOptions] = useState([]);
	const [loading, setLoading] = useState(true);

	// Fetch sub-cohort options on mount
	useEffect(() => {
		const fetchOptions = async () => {
			if (!tagId) return;

			try {
				setLoading(true);
				// signalEnabled is deliberately not passed, so the Signal-Enabled header stays 0
				// and signal audiences are left out of the options. "Other Cohorts" matches on
				// membership arctag stamps into page_json:cohorts, and a signal audience is
				// never stamped there -- no affinity rule type exists in either evaluator yet
				// (ASB-3886). Offering one would let a user build an audience that can never
				// match. Revisit when activation lands.
				const response = await getSubCohorts(tagId, amsFlavor, modelingEnabled, contextualOnly);
				const cohortsData = response?.data || {};

				const options = Object.entries(cohortsData)
					.map(([uniqueId, cohort]) => ({
						value: `${uniqueId}||${cohort.name}`,
						label: cohort.name,
						updatedon: cohort.updatedon,
					}))
					.sort((a, b) => {
						// Sort by most recently updated
						const cleanDateA = a.updatedon?.replace(/ (AM|PM)$/, "") || "";
						const cleanDateB = b.updatedon?.replace(/ (AM|PM)$/, "") || "";
						return new Date(cleanDateB) - new Date(cleanDateA);
					});

				setSubCohortOptions(options);

				// Notify parent of sub-cohort membership modes for auto-inference
				if (onSubCohortMetadataLoaded) {
					const modeMap = {};
					for (const [uniqueId, cohort] of Object.entries(cohortsData)) {
						modeMap[uniqueId] = cohort.resolved_membership_mode || "transient";
					}
					onSubCohortMetadataLoaded(modeMap);
				}
			} catch (error) {
				console.error("Failed to fetch sub-cohort options:", error);
			} finally {
				setLoading(false);
			}
		};

		fetchOptions();
	}, [tagId, amsFlavor, modelingEnabled, contextualOnly]);

	// Convert data to Select format (array of {value, label} objects)
	const getSelectedCohorts = () => {
		if (!data?.cohorts) return [];
		return data.cohorts.map((c) => ({
			value: `${c.unique_id}||${c.name || c.unique_id}`,
			label: c.name || c.unique_id,
		}));
	};

	// Handle selection change
	const handleSelectionChange = (selectedOptions) => {
		const cohorts = (selectedOptions || []).map((opt) => {
			const [unique_id, name] = opt.value.split("||");
			return { unique_id, name };
		});

		onChange({
			...data,
			cohorts,
		});
	};

	// Handle action change
	const handleActionChange = (action) => {
		onChange({
			...data,
			action: action.value,
		});
	};

	// Get current action for Radio
	const getCurrentAction = () => {
		return CONST.ruleActions.find((a) => a.value === data?.action) || CONST.ruleActions[0];
	};

	// Collapsed view - show selected items as chips
	if (isCollapsed) {
		return (
			<div className="sub-cohorts-card collapsed">
				<CriterionSummary type="subCohorts" data={data} />
			</div>
		);
	}

	// Expanded view
	return (
		<div className="sub-cohorts-card expanded">
			{/* Include/Exclude action */}
			<div className="rule-action rule-action-sub-cohort">
				<Radio
					options={CONST.ruleActions}
					name={`sub-cohort-action-${cardId}`}
					style="list"
					disabled={isReadOnly}
					required={false}
					selected={getCurrentAction()}
					onInput={handleActionChange}
				/>
			</div>

			{/* Info text */}
			<FieldGroup
				label="Target Users From Other Cohorts"
				info="Cohorts cannot target other cohorts that are already targeting cohorts."
			/>

			{/* Select for cohort selection */}
			<Select
				name="sub-cohorts"
				label="Cohorts"
				options={subCohortOptions}
				selected={getSelectedCohorts()}
				onSubmit={handleSelectionChange}
				disabled={isReadOnly || loading}
				required={false}
				multi={true}
				search={true}
				placeholder={loading ? "Loading cohorts..." : "Select cohorts..."}
			/>
		</div>
	);
}

SubCohortsCard.propTypes = {
	data: PropTypes.shape({
		cohorts: PropTypes.arrayOf(
			PropTypes.shape({
				unique_id: PropTypes.string,
				name: PropTypes.string,
			})
		),
		action: PropTypes.string,
	}),
	onChange: PropTypes.func.isRequired,
	isReadOnly: PropTypes.bool,
	isCollapsed: PropTypes.bool,
	tagId: PropTypes.string,
	amsFlavor: PropTypes.string,
	modelingEnabled: PropTypes.bool,
	cardId: PropTypes.string,
	contextualOnly: PropTypes.bool,
	onSubCohortMetadataLoaded: PropTypes.func,
};

SubCohortsCard.defaultProps = {
	data: { action: "include", cohorts: [] },
	isReadOnly: false,
	isCollapsed: false,
	tagId: null,
	amsFlavor: "full",
	modelingEnabled: false,
	cardId: "0",
	contextualOnly: false,
	onSubCohortMetadataLoaded: null,
};

// Export helper for getting selected count (used in header)
SubCohortsCard.getSelectedCount = (data) => {
	return data?.cohorts?.length || 0;
};

export default SubCohortsCard;
